import { usePage } from '@inertiajs/react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button
} from '@mui/material';
import { Analytics as AnalyticsIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { router } from '@inertiajs/react';

interface GlobalStats {
  [featureType: string]: number;
}

interface CustomerSummary {
  customer: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  last_used_at: string;
}

interface Props {
  global_stats: GlobalStats;
  anonymous_stats: GlobalStats;
  customer_only_stats: GlobalStats;
  customer_summaries: CustomerSummary[];
  [key: string]: any;
}

export default function AdminFeatureUsagePage() {
  const { props } = usePage<Props>();
  const { global_stats, anonymous_stats, customer_only_stats, customer_summaries } = props;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => router.visit('/admin/')}
          sx={{ mr: 2 }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Feature Usage Statistics
        </Typography>
      </Box>

      {/* Anonymous Feature Usage */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <AnalyticsIcon sx={{ mr: 1 }} />
            Anonymous Feature Usage
          </Typography>
          {Object.keys(anonymous_stats).filter(key => anonymous_stats[key] > 0).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No anonymous feature usage recorded yet.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {Object.entries(anonymous_stats)
                .filter(([, count]) => count > 0)
                .map(([feature, count]) => (
                  <Grid item xs={12} sm={6} md={4} key={feature}>
                    <Box sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                        {count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Customer-only Feature Usage */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <AnalyticsIcon sx={{ mr: 1 }} />
            Logged-in Customer Feature Usage
          </Typography>
          {Object.keys(customer_only_stats).filter(key => customer_only_stats[key] > 0).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No customer feature usage recorded yet.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {Object.entries(customer_only_stats)
                .filter(([, count]) => count > 0)
                .map(([feature, count]) => (
                  <Grid item xs={12} sm={6} md={4} key={feature}>
                    <Box sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                        {count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Customer Activity */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Customer Activity
      </Typography>
      {customer_summaries.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No customer activity recorded yet.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {customer_summaries.map((summary) => (
            <Grid item xs={12} sm={6} md={4} key={summary.customer.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {summary.customer.first_name} {summary.customer.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {summary.customer.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Last activity: {new Date(summary.last_used_at).toLocaleDateString()}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => router.visit(`/admin/feature_usage/customer/${summary.customer.id}`)}
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

